import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Ticket, UserCircle, SignOut, House, Barbell, Scissors, CalendarBlank, Image, GearSix, ChatCircle, Trophy, Cake, CalendarCheck, ArrowLeft, Lightning, Buildings } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import axios from "axios";
import Tickets from "./dashboard/Tickets";
import Sports from "./dashboard/Sports";
import Services from "./dashboard/Services";
import Purchases from "./dashboard/Purchases";
import Profile from "./dashboard/Profile";
import Calendar from "./dashboard/Calendar";
import Gallery from "./dashboard/Gallery";
import Chat from "./dashboard/Chat";
import Gamification from "./dashboard/Gamification";
import Birthday from "./dashboard/Birthday";
import Rentals from "./dashboard/Rentals";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MENU_ITEMS = [
  { id: "birthday", label: "Festa", icon: Cake, color: "from-[#FF5A26] to-[#E04818]", desc: "Reserve sua festa" },
  { id: "rentals", label: "Aluguéis", icon: Buildings, color: "from-blue-600 to-indigo-700", desc: "Espaços e quadras" },
  { id: "calendar", label: "Eventos", icon: CalendarCheck, color: "from-teal-500 to-cyan-600", desc: "Treinos e torneios" },
  { id: "sports", label: "Esporte", icon: Barbell, color: "from-green-500 to-emerald-600", desc: "Atividades grátis" },
  { id: "tickets", label: "Ingresso", icon: Ticket, color: "from-blue-500 to-indigo-600", desc: "Compre seu passe" },
  { id: "services", label: "Serviços", icon: Scissors, color: "from-purple-500 to-violet-600", desc: "Estética e bem-estar" },
  { id: "gamification", label: "Ranking", icon: Trophy, color: "from-yellow-500 to-amber-600", desc: "Desafios e pontos" },
  { id: "gallery", label: "Galeria", icon: Image, color: "from-pink-500 to-rose-600", desc: "Fotos e vídeos" },
  { id: "chat", label: "Chat", icon: ChatCircle, color: "from-sky-500 to-blue-600", desc: "Converse no clube" },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [activeSection, setActiveSection] = useState(null);
  const [rankInfo, setRankInfo] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/gamification/me`, { headers: getAuthHeaders(), withCredentials: true })
      .then(r => setRankInfo(r.data)).catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const goHome = () => setActiveSection(null);

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="dashboard">
      {/* Header */}
      <header className="bg-white border-b border-[#E4DFD3] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            {activeSection ? (
              <Button onClick={goHome} variant="ghost" className="text-[#115E59] rounded-full p-2" data-testid="back-home-btn">
                <ArrowLeft size={22} weight="bold" />
              </Button>
            ) : (
              <Button onClick={() => navigate("/")} variant="ghost" className="text-[#115E59] rounded-full p-2" data-testid="home-button">
                <House size={22} weight="duotone" />
              </Button>
            )}
            <h1 className="text-lg sm:text-xl font-black text-[#042F2E]">MARABU OPEN CLUB</h1>
          </div>
          <div className="flex items-center gap-2">
            {user?.is_admin && (
              <Button onClick={() => navigate("/admin")} variant="ghost" className="text-[#FF5A26] rounded-full font-bold text-xs px-3" data-testid="admin-link">
                <GearSix size={18} weight="fill" className="mr-1" /> Admin
              </Button>
            )}
            <Button onClick={() => setActiveSection("profile")} variant="ghost" className="rounded-full p-2 text-[#115E59]" data-testid="profile-btn">
              <UserCircle size={26} weight={activeSection === "profile" ? "fill" : "duotone"} />
            </Button>
            <Button onClick={handleLogout} variant="ghost" className="text-[#115E59] rounded-full p-2" data-testid="logout-button">
              <SignOut size={22} weight="duotone" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatePresence mode="wait">
          {!activeSection ? (
            /* ===== HOME GRID ===== */
            <motion.div key="home" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
              {/* Hero background */}
              <div className="relative -mx-4 sm:-mx-6 lg:-mx-8 -mt-0 mb-6 rounded-b-3xl overflow-hidden" style={{ height: "200px" }}>
                <img
                  src="https://customer-assets.emergentagent.com/job_aqua-membership-1/artifacts/eaov1h5n_WhatsApp%20Image%202026-04-07%20at%2020.51.21.jpeg"
                  alt="MARABU OPEN CLUB"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#FDFBF7] via-[#FDFBF7]/40 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5">
                  <p className="text-[#042F2E] text-sm font-medium">Olá, <strong className="text-lg">{user?.name?.split(" ")[0]}</strong></p>
                  <p className="text-[#115E59] text-xs">Bem-vindo ao MARABU OPEN CLUB</p>
                </div>
              </div>

              {/* Ranking banner */}
              {rankInfo && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => setActiveSection("gamification")}
                  className="mt-6 bg-gradient-to-r from-[#042F2E] to-[#115E59] rounded-2xl p-5 flex items-center justify-between cursor-pointer hover:shadow-lg transition-shadow"
                  data-testid="ranking-banner"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#FF5A26] rounded-xl flex items-center justify-center">
                      <Lightning size={24} weight="fill" className="text-white" />
                    </div>
                    <div>
                      <p className="text-white/70 text-xs font-bold uppercase tracking-wider">Seu Ranking</p>
                      <p className="text-white font-black text-lg">{rankInfo.total_points} pontos — Nível {rankInfo.level}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="bg-white/20 rounded-full px-4 py-2">
                      <p className="text-white font-bold text-sm">{rankInfo.unlocked_count}/{rankInfo.total_achievements}</p>
                    </div>
                    <Trophy size={20} weight="fill" className="text-yellow-400" />
                  </div>
                </motion.div>
              )}

              {/* Icon Grid */}
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-4 pb-10">
                {MENU_ITEMS.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <motion.button
                      key={item.id}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.04 }}
                      onClick={() => setActiveSection(item.id)}
                      className="flex flex-col items-center gap-2 p-5 rounded-2xl bg-white border border-[#E4DFD3] hover:shadow-lg hover:-translate-y-1 transition-all active:scale-95"
                      data-testid={`menu-${item.id}`}
                    >
                      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center shadow-md`}>
                        <Icon size={28} weight="fill" className="text-white" />
                      </div>
                      <span className="text-sm font-bold text-[#042F2E]">{item.label}</span>
                      <span className="text-[10px] text-[#115E59] leading-tight">{item.desc}</span>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          ) : (
            /* ===== SECTION CONTENT ===== */
            <motion.div key={activeSection} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }} className="py-8">
              {activeSection === "tickets" && <Tickets />}
              {activeSection === "sports" && <Sports />}
              {activeSection === "services" && <Services />}
              {activeSection === "calendar" && <Calendar />}
              {activeSection === "birthday" && <Birthday />}
              {activeSection === "rentals" && <Rentals />}
              {activeSection === "chat" && <Chat />}
              {activeSection === "gallery" && <Gallery />}
              {activeSection === "gamification" && <Gamification />}
              {activeSection === "profile" && <ProfileWithPurchases />}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// Profile wrapper that includes Purchases inside
const ProfileWithPurchases = () => {
  const [showPurchases, setShowPurchases] = useState(false);

  return (
    <div>
      {!showPurchases ? (
        <>
          <Profile />
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-6 max-w-2xl">
            <Button
              onClick={() => setShowPurchases(true)}
              className="w-full bg-white border-2 border-[#E4DFD3] hover:border-[#FF5A26] text-[#042F2E] font-bold rounded-2xl py-6 text-base justify-between px-6"
              data-testid="open-purchases-btn"
            >
              <span className="flex items-center gap-3">
                <Ticket size={22} weight="duotone" className="text-[#FF5A26]" />
                Minhas Compras
              </span>
              <span className="text-[#115E59] text-sm">Ver histórico</span>
            </Button>
          </motion.div>
        </>
      ) : (
        <div>
          <Button onClick={() => setShowPurchases(false)} variant="ghost" className="mb-4 text-[#115E59] rounded-full" data-testid="back-profile-btn">
            <ArrowLeft size={20} className="mr-2" /> Voltar ao Perfil
          </Button>
          <Purchases />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
